<template>
    <div class="d-flex flex-column">
        <step-header :title="$t('engage.overview-table.title')" button-id="popover-info" />
        <engage-popover
            :header="$t('engage.overview-table.title')"
            :intro1="$t('engage.overview-table.intro1')"
            placement="bottom"
            target="popover-info"
        />
        <div class="row">
            <div class="col-sm-7 order-2 d-flex">
                <search-bar class="ml-1" @searchValueChanged="searchValueChanged" />
                <b-dropdown
                    v-model="perPage"
                    v-b-tooltip.hover.bottom="$t('paginator.number-of-items-per-page')"
                    :text="`${$t('paginator.entries-capitalized')}: ${perPage}`"
                    class="per-page-selector mx-50 round-medium"
                    no-caret
                    variant="light"
                >
                    <b-dropdown-item
                        v-for="perPageOption in perPageOptions"
                        :key="perPageOption"
                        @click="perPage = perPageOption"
                    >
                        {{ perPageOption }}
                    </b-dropdown-item>
                </b-dropdown>
            </div>
            <div class="col-sm-5 mb-1 ml-1 ml-sm-0 mb-sm-0 d-sm-flex justify-content-end order-sm-2">
                <div class="d-flex align-items-center">
                    <b-button
                        id="new-engage-message"
                        class="round-medium"
                        variant="primary"
                        @click="$emit('create-new-engage')"
                    >
                        <font-awesome-icon class="mr-50" icon="plus" />
                        <span class="text-nowrap w-50">{{ $t("engage.new-engage-message") }}</span>
                    </b-button>
                </div>
            </div>
        </div>

        <b-table
            ref="refTable"
            :busy="isLoading"
            :empty-text="$t('paginator.no-matching-records-found')"
            :fields="tableColumns"
            :items="campaigns"
            :no-sort-reset="true"
            :sort-by.sync="sortBy"
            :sort-desc.sync="isSortDirDesc"
            class="mt-2 engage-font-md cursor-pointer"
            primary-key="id"
            responsive
            show-empty
            sticky-header="100%"
            @row-clicked="rowClicked"
        >
            <template #table-busy>
                <div class="d-flex justify-content-center my-1">
                    <b-spinner />
                </div>
            </template>
            <template #cell(name)="data">
                <TitleColumn :data="data" />
            </template>
            <template #cell(description)="data">
                <p class="m-0">{{ data.item.description }}</p>
            </template>
            <template #cell(number)="data">
                <p class="m-0">{{ getTotalContacts(data.item) }}</p>
            </template>
            <template #cell(ratio)="data">
                <DeliveredRatio :delivery-stats="data.item.deliveryStats" />
            </template>
            <!-- Column: Actions -->
            <template #cell(actions)="data">
                <ActionColumn :data="data" />
            </template>
        </b-table>

        <div class="mx-2 mb-2 mt-2">
            <b-row>
                <b-col
                    class="d-flex align-items-center justify-content-center justify-content-sm-start"
                    cols="12"
                    sm="6"
                >
                    <span class="text-muted">
                        {{ $t("paginator.showing") }} {{ dataMeta.to }} {{ $t("paginator.of") }} {{ dataMeta.of }}
                        {{ $t("paginator.entries") }}
                    </span>
                </b-col>

                <b-col class="d-flex align-items-center justify-content-center justify-content-sm-end" cols="12" sm="6">
                    <b-pagination
                        v-model="currentPage"
                        :per-page="perPage"
                        :total-rows="total"
                        class="mb-0 mt-1 mt-sm-0"
                        first-number
                        last-number
                        next-class="next-item"
                        prev-class="prev-item"
                        size="sm"
                    >
                        <template #prev-text>
                            <font-awesome-icon icon="chevron-left" size="lg" />
                        </template>
                        <template #next-text>
                            <font-awesome-icon icon="chevron-right" size="lg" />
                        </template>
                    </b-pagination>
                </b-col>
            </b-row>
        </div>
    </div>
</template>

<script>
import { useRouter } from "@/@core/utils/utils";
import { useWebsockets } from "@/libs/websockets";
import { decrypt } from "@/utils/crypto";
// @ts-ignore
import { formatDate } from "@/utils/luxon";
import { BButton, BCol, BDropdown, BDropdownItem, BPagination, BRow, BSpinner, BTable, VBTooltip } from "bootstrap-vue";
import { DateTime } from "luxon";
// @ts-ignore
import radialProgressBar from "vue-radial-progress";
import engagePopover from "../components/engagePopover.vue";
import searchBar from "../create/components/searchBar.vue";
import stepHeader from "../create/components/stepHeader.vue";
import useCampaigns from "../useCampaigns";
import { computed } from "@vue/composition-api";
import { getUserData } from "@/auth/getUserData";
import { useChannelsStore } from "@/store/useChannelsStore";
import { useUsersStore } from "@/store/useUsersStore";
import { RequestQueryBuilder } from "@rewiko/crud-request";
import TitleColumn from "./columns/TitleColumn.vue";
import ActionColumn from "@/views/modules/engage/list/columns/ActionColumn.vue";
import DeliveredRatio from "@/views/modules/engage/list/columns/DeliveredRatio.vue";

export default {
    components: {
        ActionColumn,
        BButton,
        BTable,
        BSpinner,
        BCol,
        BRow,
        BPagination,
        BDropdownItem,
        BDropdown,
        searchBar,
        stepHeader,
        engagePopover,
        radialProgressBar,
        TitleColumn,
        DeliveredRatio,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    sockets: {
        socketEvent: async function (socketMessage) {
            const wsMessage = decrypt(socketMessage.event, socketMessage.iv);
            // Do not process events that is coming from the origin sender.
            if (wsMessage.senderId === this.websockets.id) {
                return;
            }
            switch (wsMessage.type) {
                case "engage::messageSent":
                    this.updateDeliveryStats(wsMessage.payload);
                    break;
                case "ENTRY_UPDATE":
                    this.updateDeliveredStatsFromEntry(wsMessage.payload);
                    break;
            }
        },
    },
    data() {
        return {
            completedSteps: 50,
            totalSteps: 100,
            sortBy: "createdAt",
            previewMessage: "",
            isSortDirDesc: true,
            tableColumns: [
                { key: "name", label: this.$t(`engage.overview-table.message-title`), sortable: true },
                {
                    key: "description",
                    label: this.$t(`engage.overview-table.message-description`),
                    sortable: true,
                    tdClass: "text-truncate text-secondary description-max-width",
                },
                {
                    key: "number",
                    label: this.$t(`engage.overview-table.contacts-number`),
                    tdClass: "text-primary",
                    sortable: true,
                    sortByFormatted: true,
                    formatter: "getContactsCount",
                },
                {
                    key: "ratio",
                    label: this.$t(`engage.overview-table.delivered-ratio`),
                    tdClass: "m-0 p-0",
                },
                { key: "actions", label: "" },
            ],
        };
    },
    // I use context to pass the modal to the composable
    setup(_, context) {
        const { router } = useRouter();

        const {
            campaigns,
            isLoading,
            currentPage,
            perPage,
            total,
            searchQuery,
            perPageOptions,
            refTable,
            dataMeta,
            updateDeliveryStats,
            updateDeliveredStatsFromEntry,
        } = useCampaigns(context.root.$bvModal);
        const channelsStore = useChannelsStore();
        const usersStore = useUsersStore();

        usersStore.fetchUsers();

        channelsStore.fetchChannels(
            RequestQueryBuilder.create({
                join: [
                    { field: "userChannels", select: ["userId"] },
                    { field: "teamChannels", select: ["userId"] },
                ],
            })
        );

        const userData = getUserData();

        const channels = computed(() => {
            if (!channelsStore.channels) {
                return [];
            }

            return channelsStore.channels
                .map((channel) => ({
                    channelId: channel.id,
                    userChannelIds: channel.userChannels
                        .filter((user) => user.id === userData.id)
                        .map((user) => user.id),
                }))
                .filter((channel) => channel.userChannelIds.includes(userData.id));
        });

        const filteredCampaigns = computed(() => {
            if (channels.value.length > 0) {
                return campaigns.value.filter((campaign) =>
                    channels.value.map((channel) => channel.channelId).includes(campaign.channelId)
                );
            } else {
                return [];
            }
        });

        // DENNIS 2022-10: Should be part of utility
        const getFormattedDate = (date) =>
            formatDate(date, DateTime.DATE_FULL) + " " + formatDate(date, DateTime.TIME_SIMPLE);
        const searchValueChanged = (value) => (searchQuery.value = value);
        const rowClicked = (item) => router.push({ name: "engage-list-element", params: { id: item.id } });
        const getContactsCount = (value, key, item) => item?.deliveryStats?.totalContacts;

        const getTotalContacts = (item) => item?.deliveryStats?.totalContacts;

        const { websockets } = useWebsockets();

        return {
            websockets,
            searchValueChanged,
            formatDate,
            getContactsCount,
            rowClicked,
            getFormattedDate,
            updateDeliveryStats,
            updateDeliveredStatsFromEntry,
            getTotalContacts,
            currentPage,
            perPage,
            total: userData.role === "admin" || userData.role === "saysimple" ? total : filteredCampaigns.value.length,
            campaigns: userData.role === "admin" || userData.role === "saysimple" ? campaigns : filteredCampaigns,
            isLoading,
            searchQuery,
            perPageOptions,
            refTable,
            dataMeta,
        };
    },
};
</script>
